/**
 * Impersonation Context
 * 
 * Manages admin impersonation feature with ON-DEMAND loading.
 * Users list is only fetched when admin opens the dropdown, not on every login.
 */

import React, { createContext, useContext, useState, useCallback } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const ImpersonationContext = createContext();

export const ImpersonationProvider = ({ children }) => {
  // Core impersonation state
  const [impersonatedUserId, setImpersonatedUserId] = useState(null);
  const [impersonatedUser, setImpersonatedUser] = useState(null);
  const [isActualAdmin, setIsActualAdmin] = useState(false);
  const [intentionalStopImpersonation, setIntentionalStopImpersonation] = useState(false);
  
  // On-demand user list (only loaded when needed)
  const [availableUsers, setAvailableUsers] = useState([]);
  const [usersLoaded, setUsersLoaded] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);

  /**
   * Initialize admin status and restore any saved impersonation
   * Called once when admin user logs in
   */
  const initializeAdmin = useCallback(() => {
    setIsActualAdmin(true);
    
    // Restore saved impersonation from sessionStorage (if any)
    const storedImpersonation = sessionStorage.getItem('impersonatedUserId');
    if (storedImpersonation) {
      void 0 && console.log('🎭 Restoring impersonation from sessionStorage:', storedImpersonation);
      setImpersonatedUserId(storedImpersonation);
      // Set the header immediately
      axios.defaults.headers.common['X-Impersonate-User'] = storedImpersonation;
    }
  }, []);

  /**
   * Fetch users list ON-DEMAND (only when dropdown is opened)
   * This avoids the API call on every admin login
   */
  const fetchUsersOnDemand = useCallback(async () => {
    if (usersLoaded || loadingUsers) return availableUsers || [];
    
    setLoadingUsers(true);
    try {
      void 0 && console.log('🎭 Fetching users list on-demand...');
      const response = await axios.get(`${API}/api/users/list-for-impersonation`);
      const usersList = Array.isArray(response.data) ? response.data : [];
      setAvailableUsers(usersList);
      setUsersLoaded(true);
      void 0 && console.log('🎭 Users loaded:', usersList.length);
      return usersList;
    } catch (error) {
      console.error('Failed to fetch users for impersonation:', error);
      setAvailableUsers([]);
      return [];
    } finally {
      setLoadingUsers(false);
    }
  }, [usersLoaded, loadingUsers, availableUsers]);

  /**
   * Start impersonating a user
   */
  const startImpersonation = useCallback(async (userId, refreshUser, fetchData) => {
    if (!userId) return;
    
    void 0 && console.log('🎭 Starting impersonation for:', userId);
    
    // 1. Set header and save to sessionStorage
    axios.defaults.headers.common['X-Impersonate-User'] = userId;
    sessionStorage.setItem('impersonatedUserId', userId);
    setImpersonatedUserId(userId);
    setIntentionalStopImpersonation(false);
    
    // 2. Find user in available users list
    const usersList = Array.isArray(availableUsers) ? availableUsers : [];
    const foundUser = usersList.find(u => u.id === userId);
    setImpersonatedUser(foundUser || null);
    void 0 && console.log('🎭 Found user:', foundUser?.first_name, foundUser?.last_name);
    
    // 3. Refresh user context
    if (refreshUser) {
      void 0 && console.log('🎭 Calling refreshUser...');
      await refreshUser();
    }
    
    // 4. Refresh all data
    if (fetchData) {
      void 0 && console.log('🎭 Calling fetchData...');
      await fetchData();
    }
    
    void 0 && console.log('🎭 Impersonation complete');
  }, [availableUsers]);

  /**
   * Stop impersonating and restore admin view
   */
  const stopImpersonation = useCallback(async (refreshUser, fetchData) => {
    void 0 && console.log('🎭 Stopping impersonation');
    
    // Clear impersonation
    delete axios.defaults.headers.common['X-Impersonate-User'];
    sessionStorage.removeItem('impersonatedUserId');
    setImpersonatedUserId(null);
    setImpersonatedUser(null);
    setIntentionalStopImpersonation(true);
    
    // Restore original admin user
    if (refreshUser) {
      await refreshUser();
    }
    
    // Refresh data as admin
    if (fetchData) {
      await fetchData();
    }
    
    // Reset flag after a brief delay
    setTimeout(() => setIntentionalStopImpersonation(false), 100);
  }, []);

  /**
   * Clear impersonation state (for logout)
   */
  const clearImpersonation = useCallback(() => {
    delete axios.defaults.headers.common['X-Impersonate-User'];
    sessionStorage.removeItem('impersonatedUserId');
    setImpersonatedUserId(null);
    setImpersonatedUser(null);
    setIsActualAdmin(false);
    setAvailableUsers([]);
    setUsersLoaded(false);
    setIntentionalStopImpersonation(false);
  }, []);

  const value = {
    // State
    impersonatedUserId,
    impersonatedUser,
    isActualAdmin,
    availableUsers,
    loadingUsers,
    usersLoaded,
    intentionalStopImpersonation,
    
    // Actions
    initializeAdmin,
    fetchUsersOnDemand,
    startImpersonation,
    stopImpersonation,
    clearImpersonation,
    
    // For backward compatibility with existing code
    setImpersonatedUserId,
    setIntentionalStopImpersonation,
  };

  return (
    <ImpersonationContext.Provider value={value}>
      {children}
    </ImpersonationContext.Provider>
  );
};

export const useImpersonation = () => {
  const context = useContext(ImpersonationContext);
  if (!context) {
    throw new Error('useImpersonation must be used within ImpersonationProvider');
  }
  return context;
};

export default ImpersonationContext;
